import { useState, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface PauseSubscriptionResult {
  success: boolean;
  message?: string;
  error?: string;
}

export const usePauseSubscription = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pauseSubscription = useCallback(async (): Promise<PauseSubscriptionResult> => {
    if (!isSupabaseConfigured || !supabase) {
      const errorMsg = 'Supabase not configured';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const errorMsg = 'Not authenticated';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || supabase.supabaseUrl;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || supabase.supabaseKey;
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/pause-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseKey,
          },
          body: JSON.stringify({
            action: 'pause',
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to pause subscription');
      }

      if (result.success) {
        toast.success('Abonnement mis en pause avec succès');
        return { success: true, message: result.message };
      }

      return { success: false, error: result.error || 'Unknown error' };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to pause subscription';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const resumeSubscription = useCallback(async (): Promise<PauseSubscriptionResult> => {
    if (!isSupabaseConfigured || !supabase) {
      const errorMsg = 'Supabase not configured';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }

    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        const errorMsg = 'Not authenticated';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || supabase.supabaseUrl;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || supabase.supabaseKey;
      
      const response = await fetch(
        `${supabaseUrl}/functions/v1/pause-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': supabaseKey,
          },
          body: JSON.stringify({
            action: 'resume',
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to resume subscription');
      }

      if (result.success) {
        toast.success('Abonnement repris avec succès');
        return { success: true, message: result.message };
      }

      return { success: false, error: result.error || 'Unknown error' };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resume subscription';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    pauseSubscription,
    resumeSubscription,
    loading,
    error,
  };
};

